<template>
  <b-container fluid class="mt-3">
    <b-row class="mt-3">
      <b-col cols="4">
        <b-card title="Request Properties">
          <b-form-group label="OneLogin Subdomain" label-size="sm">
            <b-form-input id="subdomain"
                          type="text"
                          v-model="subdomain"
                          placeholder="Enter your OneLogin subdomain">
            </b-form-input>
          </b-form-group>
          <b-form-group label="Issuer" label-size="sm">
            <b-form-select v-model="issuerURI" :options="issuerOptions"/>
          </b-form-group>
          <b-form-group label="Client" label-size="sm">
            <b-form-select v-model="clientID" :options="clientOptions"/>
          </b-form-group>
          <b-form-group label="Grant Type" label-size="sm">
            <b-form-select v-model="grantType" :options="grantOptions"/>
          </b-form-group>
          <b-form-group label="Token Authentication Method" label-size="sm" v-if="grantType == 'code'">
            <b-form-select v-model="tokenAuthMethod" :options="tokenAuthOptions" />
          </b-form-group>
          <b-form-group v-if="tokenAuthMethod == 'none'" label="Code Verifier" label-size="sm">
            <b-form-input id="codeVerifier"
                          type="text"
                          v-model="codeVerifier"
                          placeholder="A random string">
            </b-form-input>
          </b-form-group>
          <b-form-group label="Response Type" label-size="sm">
            <b-form-input id="responseType"
                          type="text"
                          required
                          v-model="responseType"
                          placeholder="">
            </b-form-input>
          </b-form-group>
          <b-form-group label="Scope" label-size="sm">
            <b-form-input id="scope"
                          type="text"
                          required
                          v-model="scope"
                          placeholder="Requires minimum of openid scope">
            </b-form-input>
          </b-form-group>
          <b-form-group label="Resource (optional)" label-size="sm">
            <b-form-input id="resource"
                          type="text"
                          required
                          v-model="resource"
                          placeholder="API Resource Identifier">
            </b-form-input>
          </b-form-group>
          <b-form-group label="State" label-size="sm">
            <b-form-input id="state"
                          type="text"
                          required
                          v-model="state"
                          placeholder="">
            </b-form-input>
          </b-form-group>
          <b-form-group label="Nonce" label-size="sm">
            <b-form-input id="nonce"
                          type="text"
                          required
                          v-model="nonce"
                          placeholder="">
            </b-form-input>
          </b-form-group>
          <b-button type="button" variant="danger" v-on:click="reset">Reset</b-button>
        </b-card>
      </b-col>
      <b-col cols="8">
        <b-card title="Request Preview">
          <b-tabs>
            <b-tab title="1" v-if="requestURI" class="m-3">
              <div class="wrap">
                <b-alert show variant="info">Clicking "Authenticate" will redirect the browser to the following URL and start the flow.</b-alert>
                <p><b>Request Url:</b></p>
                {{ requestURI }}
                <p v-if="tokenAuthMethod == 'none'" class="mt-3"><b>Code Challenge:</b> {{ codeChallenge }}</p>
              </div>
            </b-tab>
            <b-tab title="2" class="m-3" v-if="grantType == 'code'" :active="authorizationCode != null" >
              <b-alert show variant="info">In the second step of the flow the Authorization Code returned from the first request is exchanged for an Access Token.</b-alert>
              <div class="wrap">
                <pre>{{ accessTokenParams | pretty }}</pre>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
        <b-container class="mt-3 mb-3">
          <b-button type="button" variant="success" v-on:click="authenticate" v-if="readyToAuth" :disabled="authorizationCode != null || flowComplete">Authenticate</b-button>
          <b-button type="button" variant="success" v-on:click="getAccessToken" v-if="grantType == 'code'" :disabled="!authorizationCode || flowComplete" >Get Access Token</b-button>
        </b-container>
        <b-alert variant="danger" :show="error != null">
          <h4>{{ error }}</h4>
          <hr/>
          <p>
            {{ errorDescription }}
          </p>
        </b-alert>
        <b-card title="Response">
          <b-alert :show="flowComplete" variant="success">Success! The grant is complete</b-alert>
          <b-tabs>
            <b-tab title="Authorization Code" v-if="authorizationCode && grantType == 'code'" class="m-3" >
              <p class="wrap">{{ authorizationCode }}</p>
            </b-tab>
            <b-tab title="ID Token" active v-if="token" class="m-3">
              <h4>Header</h4>
              <pre>{{ token.header | pretty }}</pre>
              <hr />
              <h4>Payload</h4>
              <pre>{{ token.payload | pretty }}</pre>
              <hr />
              <h4>Signature</h4>
              {{ token.signature }}
            </b-tab>
            <b-tab title="Access Token" v-if="accessToken" class="m-3">
              <p><strong>Expires In:</strong> {{ accessTokenExpiresIn }} seconds</p>
              <p><strong>Token Type:</strong> {{ accessTokenType }}</p>
              <p><strong>Is JWT:</strong> {{ jwtAccessToken ? 'Yes' : 'No' }}</p>
              <hr/>
              <p v-if="!jwtAccessToken">{{ accessToken }}</p>
              <div title="Access Token" active v-if="jwtAccessToken" class="m-3">
                <h4>Header</h4>
                <pre>{{ jwtAccessToken.header | pretty }}</pre>
                <hr />
                <h4>Payload</h4>
                <pre>{{ jwtAccessToken.payload | pretty }}</pre>
                <hr />
                <h4>Signature</h4>
                {{ jwtAccessToken.signature }}
              </div>
            </b-tab>
            <b-tab title="Refresh Token" v-if="refreshToken" class="m-3">
              {{ refreshToken }}
            </b-tab>
          </b-tabs>
        </b-card>

        <div>
          {{ responseHeaders }}
          {{ responseURI }}
          {{ responseBody }}
        </div>
      </b-col>
    </b-row>

  </b-container>
</template>

<script>
export default {
  data () {
    return {
      redirectURI: window.location.origin + '/openid-connect/inspector',
      clientID: null,
      clientSecret: null,
      clients: [],
      clientOptions: [],
      issuerURI: 'https://' + this.subdomain + '.onelogin.com/oidc/2', // Default
      grantType: 'implicit', // Default
      grantOptions: [
        { value: 'implicit', text: 'Implicit Grant'},
        { value: 'code', text: 'Authorization Code Grant'}
        // { value: 'client_credentials', text: 'Client Credentials Grant'},
        // { value: 'password', text: 'Resource Owner Password Grant'},
      ],
      tokenAuthMethod: 'basic', // Default
      tokenAuthOptions: [
        { value: 'basic', text: 'Basic'},
        { value: 'post', text: 'POST'},
        { value: 'none', text: 'None (PKCE)'}
      ],
      responseType: 'id_token', // Default
      requestBody: null,
      responseURI: null,
      responseBody: null,
      responseHeaders: null,
      scope: 'openid',
      codeVerifier: null,
      codeChallenge: null,
      resource: null,
      idToken: null,
      accessToken: null,
      accessTokenExpiresIn: null,
      accessTokenType: null,
      refreshToken: null,
      authorizationCode: null,
      nonce: this.generateUUID(),
      state: this.generateUUID(),
      error: null,
      errorDescription: null,
      subdomain: localStorage['ol_subdomain']
    }
  },

  computed: {
    issuerOptions: function() {
      let subdomain = this.subdomain || 'your-subdomain-domain'

      return [
        { value: 'https://' + subdomain + '.onelogin.com/oidc/2', text: 'https://' + subdomain + '.onelogin.com/oidc/2 (Latest)'},
        { value: 'https://openid-connect.onelogin.com/oidc', text: 'US - https://openid-connect.onelogin.com/oidc (Deprecated)'},
        { value: 'https://openid-connect-eu.onelogin.com/oidc', text: 'EU - https://openid-connect-eu.onelogin.com/oidc (Deprecated)'}
      ]
    },

    requestURI: {
      get: function () {
        var uri = this.issuerURI + '/auth?scope=' + this.scope + '&client_id=' + this.clientID
        + '&redirect_uri=' + this.redirectURI + '&state=' + this.state + '&nonce=' + this.nonce
        + '&response_type=' + this.responseType

        if (this.resource) {
          uri = uri + '&resource=' + this.resource
        }

        if (this.tokenAuthMethod == 'none') {
          uri = uri + '&code_challenge_method=S256&code_challenge=' + this.codeChallenge
        }

        return uri
      },
      set: function(){

      }
    },

    token: function () {
      if (!this.idToken) return null
      return this.decodeJWT(this.idToken)
    },

    jwtAccessToken: function () {
      if (this.accessToken.split('.').length != 3) return null
      var jwt = this.decodeJWT(this.accessToken)
      return jwt
    },

    readyToAuth: function () {
      return this.clientID && this.grantType && this.scope
    },

    flowComplete: function () {
      switch (this.grantType) {
        case 'implicit':
          return this.token != null

        case 'code':
          return this.accessToken != null

        default:
          return false
      }
    },

    accessTokenParams: function () {
      var params = {
        "uri": this.issuerURI + "/token",
        "headers": {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        "form": {
          "grant_type": "authorization_code",
          "code": this.authorizationCode,
          "client_id": this.clientID,
          "redirect_uri": this.redirectURI
        }
      }

      switch (this.tokenAuthMethod) {
        case 'basic':
          params.headers["Authorization"] = 'Basic ' + window.btoa(this.clientID + ":" + this.clientSecret)
          break

        case 'post':
          params.form["client_secret"] = this.clientSecret
          break

        case 'none':
          params.form["code_verifier"] = this.codeVerifier
          break
      }

      return params
    }
  },

  watch: {
    subdomain: function () {
      localStorage['ol_subdomain'] = this.subdomain
    },

    clientID: function (val) {
      var client = this.clients.find(c => c.ID == val)

      if(client){
        this.clientSecret = client.secret
      }
    },

    clientOptions: function () {
      // Set default client on page load
      if (!this.clientID && this.clients.length > 0) {
        this.clientID = this.clients[0].ID
      }
    },

    grantType: function () {
      switch (this.grantType) {
        case 'implicit':
          this.responseType = 'id_token'
          break

        case 'code':
          this.responseType = 'code'
          break
      }
    },

    codeVerifier: function () {
      this.getCodeChallenge()
    },

    accessToken: function() {
      localStorage["ol_accessToken"] = this.accessToken
    }
  },

  created () {
  },

  mounted () {
    if (localStorage.clients) {
      this.clients = JSON.parse(localStorage.clients) || [];

      this.clientOptions = this.clients.map(c => {
        return {
          value: c.ID,
          text: c.name
        }
      })
    }

    // Get hash return for implicit flow
    var hash = this.getHash()

    if (hash) {
      this.idToken = hash.id_token
      this.loadState(hash.state)
      // If implicit flow with token supplied as response type
      this.accessToken = hash.access_token
      this.accessTokenExpiresIn = hash.expires_in
      this.accessTokenType = hash.token_type
      this.error = hash.error
      if (this.error) {
        this.errorDescription = decodeURI(hash.error_description)
      }
    }

    // Get querystring return for auth flow\
    var query = this.$route.query
    if (Object.keys(query).length > 0) {
      this.authorizationCode = query.code || null
      this.loadState(query.state)
      this.error = query.error || null
      if (this.error) {
        this.errorDescription = decodeURI(query.error_description)
      }
    }

    if (query || hash) {
      this.saveState()
    }
  },

  methods: {
    /**
     * Starts the Implicit or Auth flows with a redirect to login page
     */
    authenticate: function () {
      this.saveState()
      window.location = this.requestURI
    },

    /**
     * The second stage in the Auth Code flow where we
     * swap the authorization code for an access token.
     *
     * If you are looking at this code please realize that it is
     * only for demonstration purposes. You should never make
     * this call from client side javascript as it exposes your
     * client_secret.
     */
    getAccessToken: function () {
      this.error = null

      // Due to CORS restrictions and the fact that this request should be made
      // as a server side call we are using a proxy to send the request to OneLogin
      this.$http.post('https://y4m1jyvl8a.execute-api.us-east-1.amazonaws.com/dev/proxy', this.accessTokenParams, {}).then(res => {
        var body = JSON.parse(res.body)

        this.accessToken = body.access_token
        this.accessTokenExpiresIn = body.expires_in
        this.accessTokenType = body.token_type
        this.refreshToken = body.refresh_token
        this.idToken = body.id_token
        this.saveState()

      }, err => {
        if (err.body.error) {
          var msg = JSON.parse(err.body.error)
          this.error = msg.error
          this.errorDescription = msg.error_description
        }
      })
    },

    getCodeChallenge: function () {
      this.$http.post('https://y4m1jyvl8a.execute-api.us-east-1.amazonaws.com/dev/pkce', {
          "codeVerifier": this.codeVerifier
        },
        {}).then(res => {
          console.log(res)

          this.codeChallenge = res.body

      }, err => {
        console.log(err)
      })
    },

    reset: function () {
      this.refreshToken = null
      this.accessToken = null
      this.idToken = null
      this.authorizationCode = null
      this.nonce = this.generateUUID()
      this.state = this.generateUUID()
      this.error = null
      this.errorDescription = null
      this.codeVerifier = null
    },

    getStorage: function (key) {
      var data = localStorage[key]
      if (!data) return null
      return JSON.parse(data)
    },

    setStorage: function (key, data) {
      localStorage[key] = JSON.stringify(data)
    },

    loadState: function (state) {
      if (state) {
        try {
          var txn = this.getStorage(state)

          if(txn){
            this.issuerURI = txn.issuerURI
            this.clientID = txn.clientID
            this.clientSecret = txn.clientSecret
            this.grantType = txn.grantType
            this.scope = txn.scope
            this.state = txn.state
            this.nonce = txn.nonce
            this.resource = txn.resource
            this.requestURI = txn.requestURI
            this.tokenAuthMethod = txn.tokenAuthMethod
            this.responseType = txn.responseType
            this.codeVerifier = txn.codeVerifier
            this.subdomain = txn.subdomain
          }
        } catch (err) {
          console.log('BAD STATE'. err)
        }
      }
    },

    saveState: function () {
      var params = {
        issuerURI: this.issuerURI,
        clientID: this.clientID,
        clientSecret: this.clientSecret,
        grantType: this.grantType,
        responseType: this.responseType,
        scope: this.scope,
        state: this.state,
        nonce: this.nonce,
        resource: this.resource,
        requestURI: this.requestURI,
        tokenAuthMethod: this.tokenAuthMethod,
        accessToken: this.accessToken,
        refreshToken: this.refreshToken,
        token: this.token,
        idToken: this.idToken,
        authorizationCode: this.authorizationCode,
        success: this.flowComplete,
        error: this.error,
        errorDescription: this.errorDescription,
        codeVerifier: this.codeVerifier,
        subdomain: this.subdomain
      }
      this.setStorage(this.state, params)
    },

    generateUUID: function () {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },

    getHash: function () {
      if (!this.$route.hash) return null

      var params = {}
      var hash = this.$route.hash.replace('#', '').split('&')
      for(var i=0; i<hash.length; i++){
        var param = hash[i].split('=')
        params[param[0]] = param[1]
      }
      return params
    },

    decodeJWT: function (jwt) {
      var parts = jwt.split('.');

      var header = parts[0]
      header = header.replace('-', '+').replace('_', '/');

      var payload = parts[1]
      payload = payload.replace('-', '+').replace('_', '/');

      return {
        header: this.base64UrlDecode(header),
        payload: this.base64UrlDecode(payload),
        signature: parts[2]
      }
    },

    base64UrlDecode: function (val) {
      var base64 = val.replace('-', '+').replace('_', '/')
      return window.atob(base64)
    }
  },

  filters: {
    pretty: function(value) {
      var json = value

      if (typeof(value) == 'string') {
        json = JSON.parse(value)
      }

      return JSON.stringify(json, null, 2);
    }
  }
}
</script>

<style>
  .wrap {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  h4 {
    font-size: 1em;
  }
</style>
